import { useState } from "react";
import { Send, Loader2, Bell, AlertTriangle } from "lucide-react";
import { api } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";

/* AdminBroadcastsTab
 * ------------------
 * Compose-and-send screen for Web Push broadcasts. Backend: POST
 * /api/admin/push/broadcast with { title, body, url, audience }. Confirms
 * before sending because every call dispatches the message (no preview run).
 */

const URL_SUGGESTIONS = [
  { label: "Inicio", url: "/" },
  { label: "Buscar autos", url: "/search" },
  { label: "Guía RivoNes", url: "/guia" },
  { label: "Publicar mi auto", url: "/host/dashboard" },
  { label: "Mis viajes", url: "/trips" },
];

const TITLE_MAX = 60;
const BODY_MAX = 160;

export default function AdminBroadcastsTab() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("/");
  const [audience, setAudience] = useState("marketing");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const canSend = title.trim().length > 0 && body.trim().length > 0 && !sending;

  const handleSend = async () => {
    setSending(true);
    setConfirmOpen(false);
    try {
      const { data } = await api.post("/admin/push/broadcast", {
        title: title.trim(),
        body: body.trim(),
        url: url.trim() || "/",
        audience,
      });
      setLastResult(data);
      toast.success(
        `Entregado a ${data.delivered} de ${data.audience_size} usuarios`,
      );
      setTitle("");
      setBody("");
    } catch (e) {
      const msg = e?.response?.data?.detail || "No pudimos enviar el broadcast";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-broadcasts-tab">
      <div>
        <h2 className="font-heading text-2xl font-semibold text-agave">
          Push broadcasts
        </h2>
        <p className="text-sm text-neutral-600 mt-1">
          Envía una notificación push a todos los usuarios suscritos. Llega a
          cualquier dispositivo (Android, iOS instalado como app, desktop) con
          la app cerrada o en segundo plano.
        </p>
      </div>

      <div className="card-base">
        <div className="grid gap-5">
          {/* Title */}
          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <Label htmlFor="bc-title">Título</Label>
              <span className="text-xs text-neutral-500">
                {title.length}/{TITLE_MAX}
              </span>
            </div>
            <Input
              id="bc-title"
              data-testid="broadcast-title-input"
              value={title}
              maxLength={TITLE_MAX}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="¡Rivones lanza en 30 días! 🚗"
            />
          </div>

          {/* Body */}
          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <Label htmlFor="bc-body">Mensaje</Label>
              <span className="text-xs text-neutral-500">
                {body.length}/{BODY_MAX}
              </span>
            </div>
            <Textarea
              id="bc-body"
              data-testid="broadcast-body-input"
              value={body}
              maxLength={BODY_MAX}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Reserva tu primera renta con $200 de descuento. Cupos limitados."
              rows={3}
            />
          </div>

          {/* URL */}
          <div>
            <Label htmlFor="bc-url">URL al tocar</Label>
            <Input
              id="bc-url"
              data-testid="broadcast-url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="/"
              className="mt-1.5"
            />
            <div className="flex flex-wrap gap-1.5 mt-2">
              {URL_SUGGESTIONS.map((s) => (
                <button
                  key={s.url}
                  type="button"
                  onClick={() => setUrl(s.url)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition ${
                    url === s.url
                      ? "bg-terracotta text-white border-terracotta"
                      : "border-neutral-300 text-neutral-700 hover:bg-neutral-50"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Audience */}
          <div>
            <Label>Audiencia</Label>
            <RadioGroup
              value={audience}
              onValueChange={setAudience}
              className="mt-2 grid sm:grid-cols-2 gap-2"
              data-testid="broadcast-audience"
            >
              <label
                className={`cursor-pointer rounded-xl border p-3 flex gap-3 items-start transition ${
                  audience === "marketing"
                    ? "border-terracotta bg-terracotta/5"
                    : "border-neutral-200 hover:border-neutral-300"
                }`}
              >
                <RadioGroupItem value="marketing" id="aud-marketing" />
                <div>
                  <div className="font-medium text-sm">Marketing opt-in</div>
                  <p className="text-xs text-neutral-600 mt-0.5">
                    Solo usuarios con consentimiento de marketing activo.
                    Recomendado para promociones y lanzamientos.
                  </p>
                </div>
              </label>
              <label
                className={`cursor-pointer rounded-xl border p-3 flex gap-3 items-start transition ${
                  audience === "all"
                    ? "border-terracotta bg-terracotta/5"
                    : "border-neutral-200 hover:border-neutral-300"
                }`}
              >
                <RadioGroupItem value="all" id="aud-all" />
                <div>
                  <div className="font-medium text-sm">Todos los usuarios</div>
                  <p className="text-xs text-neutral-600 mt-0.5">
                    Cualquiera con la app instalada y notificaciones activas.
                    Reserva para avisos de servicio críticos.
                  </p>
                </div>
              </label>
            </RadioGroup>
          </div>

          {/* Preview */}
          {(title || body) && (
            <div className="rounded-xl bg-charcoal text-sand p-4 ring-1 ring-white/10">
              <div className="text-[10px] uppercase tracking-wider text-sand/50 mb-2">
                Vista previa
              </div>
              <div className="flex gap-3 items-start">
                <div className="w-10 h-10 rounded-xl bg-terracotta/90 flex items-center justify-center shrink-0">
                  <Bell className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-[14px] truncate">
                    {title || "Título"}
                  </div>
                  <div className="text-[13px] text-sand/80 line-clamp-2">
                    {body || "Mensaje del broadcast…"}
                  </div>
                  <div className="text-[11px] text-sand/50 mt-1">
                    Rivones · ahora
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Send */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-amber-700 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" />
              Cada envío es definitivo — no hay deshacer.
            </p>
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={!canSend}
              data-testid="broadcast-send-btn"
              className="bg-terracotta hover:bg-terracotta/90"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Enviar broadcast
            </Button>
          </div>
        </div>
      </div>

      {lastResult && (
        <div className="card-base" data-testid="broadcast-last-result">
          <h3 className="font-heading font-semibold text-agave">
            Último envío
          </h3>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-neutral-500">Audiencia</div>
              <div className="text-xl font-semibold text-agave">
                {lastResult.audience_size}
              </div>
            </div>
            <div>
              <div className="text-neutral-500">Entregados</div>
              <div className="text-xl font-semibold text-terracotta">
                {lastResult.delivered}
              </div>
            </div>
          </div>
          <p className="text-xs text-neutral-500 mt-2">
            La diferencia se debe a usuarios sin suscripciones activas o con
            navegadores temporalmente fuera de línea.
          </p>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Enviar este broadcast?</AlertDialogTitle>
            <AlertDialogDescription>
              Se enviará una notificación push inmediata a la audiencia
              seleccionada (<strong>{audience === "marketing" ? "Marketing opt-in" : "Todos los usuarios"}</strong>).
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="broadcast-confirm-cancel">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSend}
              data-testid="broadcast-confirm-send"
              className="bg-terracotta hover:bg-terracotta/90"
            >
              Sí, enviar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
